import { IconSpark } from '../icons';

export interface JobRow {
  id: string;
  type: string;
  phase: string | null;
  status: 'queued' | 'running' | 'done' | 'error' | 'cancelled';
  createdAt: string;
  payload?: { mode?: string; prompt?: string; autofix?: boolean; error?: { kind?: string } };
}

export interface CareCheckStatus {
  phase: 'playtest' | 'snapshot' | 'review';
}

/**
 * Background-work indicator (spec 35d). Lives at the bottom of the right rail, UNDER the AI thread —
 * everything the single shared AI is doing that the learner is NOT actively blocked on: auto-fixes,
 * the visual "care check", edits after the first build. The first build alone still earns the
 * full-screen modal; everything after runs here so the learner keeps working.
 *
 * The box-api runner is a single global thread (one job at a time, createdAt order), so this list —
 * the same one every collaborator polls — IS the shared queue. When several are lined up we show the
 * running one plus how many are waiting behind it. Stopping is always free (D8).
 */

export const CARE_WORDS: Record<CareCheckStatus['phase'], string> = {
  snapshot: 'Taking pictures of your screens',
  playtest: 'Test-playing your game to catch problems early',
  review: 'Looking over the pictures for anything broken',
};

// rough phase → fraction so the bar advances honestly without pretending to be precise
export const PHASE_PCT: Record<string, number> = {
  plan: 12, html: 32, images: 52, css: 68, js: 85, persist: 95,
  scope: 20, delta: 62, done: 99,
};

export function jobLabel(job: JobRow): string {
  const p = job.payload ?? {};
  if (p.autofix) {
    return p.error?.kind === 'visual' || p.error?.kind === 'layout'
      ? 'Fixing how it looks'
      : 'Fixing an issue Sparky found';
  }
  if (job.type === 'feedback') return 'Applying your review notes';
  if (job.type === 'generate') return 'Building your app';
  if (job.type === 'edit') return p.prompt ? `Changing: ${p.prompt.slice(0, 48)}` : 'Making your change';
  return 'Working';
}

function Bar({ pct, indeterminate }: { pct: number; indeterminate?: boolean }) {
  return (
    <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-page">
      <div
        className={indeterminate ? 'h-full w-1/3 animate-pulse rounded-full bg-accent' : 'h-full rounded-full bg-accent transition-all duration-500'}
        style={indeterminate ? undefined : { width: `${Math.max(6, pct)}%` }}
      />
    </div>
  );
}

export function BackgroundWork({
  jobs,
  care,
  onStop,
  onCancelCare,
}: {
  jobs: JobRow[];
  care?: CareCheckStatus | null;
  onStop: (jobId: string) => void;
  onCancelCare?: () => void;
}) {
  const active = jobs.filter(j => j.status === 'running' || j.status === 'queued');
  if (active.length === 0 && !care) return null;

  const running = active.find(j => j.status === 'running') ?? active[0] ?? null;
  const waiting = active.filter(j => j !== running).length;

  return (
    <div className="shrink-0 border-t border-line bg-surface px-3 py-2.5">
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted">
        <span className="text-accent">
          <IconSpark width={12} height={12} />
        </span>
        Sparky is working in the background
      </div>

      {care && (
        <div className="flex items-start gap-2.5 py-1">
          <span className="mt-1 h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-accent" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold">{CARE_WORDS[care.phase]}…</p>
            <Bar pct={0} indeterminate />
          </div>
          <button
            type="button"
            onClick={onCancelCare}
            className="shrink-0 rounded-lg border border-line px-2 py-0.5 text-[11px] font-semibold text-muted transition hover:text-ink"
          >
            Stop
          </button>
        </div>
      )}

      {running && (
        <div className="flex items-start gap-2.5 py-1">
          <span className="mt-1 h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-accent" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold">{jobLabel(running)}</p>
            <Bar pct={PHASE_PCT[running.phase ?? ''] ?? 8} indeterminate={running.status === 'queued'} />
            {waiting > 0 && (
              <p className="mt-1 text-[11px] text-muted">
                {waiting} more {waiting === 1 ? 'request' : 'requests'} waiting in line
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => onStop(running.id)}
            className="shrink-0 rounded-lg border border-line px-2 py-0.5 text-[11px] font-semibold text-muted transition hover:text-ink"
          >
            Stop
          </button>
        </div>
      )}

      <p className="mt-1.5 text-[10px] leading-snug text-muted">
        This runs on its own so you can keep working — all requests share one AI, so they take turns
        instead of clashing.
      </p>
    </div>
  );
}
